import { useQuery } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { Button, CategoryVisual, EmptyState, Skeleton, Tile, pickText } from '@aps/ui';
import { useNavigation } from '../app/navigation.js';
import { useSettings } from '../app/settings.js';
import { api, publicMediaUrl } from '../lib/api.js';

/**
 * "Barcha kategoriyalar": bosh sahifadagi "Boshqalar" plitkasi shu yerga olib keladi.
 * Mijoz "yana qanday kategoriya bor ekan" deb bosadi — unga tovar emas, to'liq
 * kategoriyalar ro'yxati (tovar soni bilan) ko'rinishi kerak. Tovari yo'q kategoriya
 * bosh sahifadagi kabi yashiriladi.
 */
export function CategoriesScreen(): ReactNode {
  const { t, locale } = useSettings();
  const { push } = useNavigation();
  const categories = useQuery({ queryKey: ['categories'], queryFn: api.categories });
  const visible = (categories.data ?? []).filter((category) => category.productCount !== 0);

  return (
    <div className="aps-screen">
      <header className="aps-screen__header">
        <h1 className="aps-screen__title">{t('categories.title')}</h1>
      </header>

      {categories.isPending ? (
        <div className="aps-grid-2">
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton key={index} height="102px" />
          ))}
        </div>
      ) : categories.isError ? (
        <div role="alert">
          <EmptyState
            title={t('common.error')}
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  void categories.refetch();
                }}
              >
                {t('common.retry')}
              </Button>
            }
          />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState title={t('catalog.empty')} />
      ) : (
        <div className="aps-grid-2">
          {visible.map((category) => (
            <Tile
              key={category.id}
              categoryKey={category.iconKey ?? 'access'}
              icon={
                <CategoryVisual
                  iconKey={category.iconKey}
                  imageUrl={publicMediaUrl(category.imageUrl)}
                  size={40}
                />
              }
              label={pickText(category.nameUz, category.nameRu, locale)}
              {...(category.productCount === undefined ? {} : { count: category.productCount })}
              onSelect={() => {
                push({
                  name: 'catalog',
                  categoryId: category.id,
                  title: pickText(category.nameUz, category.nameRu, locale),
                });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
